import { Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { environment } from '@env/environment';
import { Observable } from 'rxjs';

import { SignerService } from './signer/signer.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs/internal/observable/throwError';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class HttpService {
  private apiBaseUrl;
  private headers: Headers;

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private signerService: SignerService,
    private translate: TranslateService,
  ) {
    this.apiBaseUrl = environment.apiUrl;
  }

  getData(url: string, params?: any, isSkipExpired = false): Observable<any> {
    return this.httpRequest(url, 'GET', isSkipExpired);
  }

  postData(url: string, model?: any, isSkipExpired = false): Observable<any> {
    return this.httpRequest(url, 'POST', isSkipExpired, model, null);
  }

  putData(url: string, model: any, isSkipExpired = false): Observable<any> {
    const body = JSON.stringify(model);
    return this.httpRequest(url, 'PUT', isSkipExpired, body);
  }

  deleteData(url: string, isSkipExpired = false): Observable<any> {
    return this.httpRequest(url, 'DELETE', isSkipExpired);
  }

  postArrayBufferPromise(url, model?: any, isSkipExpired = false, observe?: string): any {
    const body = JSON.stringify(model);
    return this.httpRequest(url, 'POST', isSkipExpired, body, true, observe).toPromise();
  }

  postFormData(url: string, file: File, partName: string = 'image', isSkipExpired = false): Observable<any> {
    const body = new FormData();
    body.append(partName, file, file.name);
    return this.httpRequest(url, 'POST', isSkipExpired, body);
  }

  putFormData(url: string, file: File, partName: string = 'image', isSkipExpired = false, responseContentType = null): Observable<any> {
    const body = new FormData();
    body.append(partName, file, file.name);
    return this.httpRequest(url, 'PUT', isSkipExpired, body, null, null, 'text');
  }

  downloadFile(url: string, params?: any, isSkipExpired = false) {
    if (!isSkipExpired && this.signerService.checkExpiryDate()) {
      this.signerService.closeSession();
    }

    const signerInfo = this.signerService.get();

    if (!signerInfo) {
      return;
    }

    window.location.href = this.url(url) + '/?access_token=' + signerInfo['access_token'];
  }

  private httpRequest(relativeUrl: string, method: 'POST' | 'GET' | 'PUT' | 'DELETE', isSkipExpired: any, body?: any, isArrayBuffer?: boolean, observe?: string, isText?: string): Observable<any> {
    if (!isSkipExpired && this.signerService.checkExpiryDate()) {
      this.signerService.closeSession();
      return Observable.throw('session has timed out');
    }

    let headers = new HttpHeaders();
    headers = this.appendAccessToken(headers);

    if (!(body instanceof FormData)) {
      headers = headers.append('Content-Type', 'application/json');
    }

    if (method === 'GET') {
      headers = headers.append('Cache-Control', 'no-cache');
    }

    let request$: Observable<any>;
    const options: any = { headers: headers };

    if (method === 'GET') {
      request$ = this.httpClient.get(this.url(relativeUrl), options);
    } else if (method === 'POST') {
      if (isArrayBuffer) {
        options['responseType'] = 'arraybuffer' as 'json';
      }

      if (observe) {
        options['observe'] = observe;
      }
      request$ = this.httpClient.post(this.url(relativeUrl), body, options);
    } else if (method === 'PUT') {
      if (isText) {
        options['responseType'] = 'text';
      }

      request$ = this.httpClient.put(this.url(relativeUrl), body, options);
    } else if (method === 'DELETE') {
      request$ = this.httpClient.delete(this.url(relativeUrl), options);
    }

    return request$.pipe(
      catchError(this.handleError)
    );
  }

  private appendAccessToken(headers: HttpHeaders): HttpHeaders {
    const signerInfo = this.signerService.get();
    headers = headers.delete('Authorization');

    if (!signerInfo) {
      return headers;
    }

    headers = headers.append('Authorization', `${signerInfo['token_type']} ${signerInfo['access_token']}`);
    return headers;
  }

  private url(url: string): string {
    return this.apiBaseUrl + url;
  }

  private handleError = (error: any) => {
    if (error.status === 401) {
      if (this.signerService.checkExpiryDate()) {
        this.signerService.closeSession();
      } else {
        this.router.navigate(['/error']);
      }
    } else if (error.status === 400) {
      const errorBody = error.error;

      if (errorBody) {
        const lang = errorBody.language;

          this.translate.use(lang);
          this.translate.setDefaultLang(lang);

        const packageErrors = ['PACKAGE_DRAFTED', 'PACKAGE_DECLINED', 'PACKAGE_EXPIRED', 'PACKAGE_ARCHIVED', 'PACKAGE_TRASHED', 'PACKAGE_NOT_FOUND'];
        if (packageErrors.indexOf(errorBody.error) > -1) {
          this.router.navigate(
            [
              '/package-error',
              {
                error: errorBody.error,
                error_description: errorBody.error_description,
              },
            ],
            { relativeTo: this.route, skipLocationChange: true },
          );
        }
      }
    } else if (error.status === 500) {
      this.router.navigate(['/error']);
    }

    return throwError(error);
  };

  private getErrorJson = (error: any): any => {
    let body;

    try {
      body = error.json();
    } catch (exeption) {
      body = error.text('iso-8859'); // UTF-8
      try {
        body = JSON.parse(body);
      } catch (ex) {}
    }

    return body;
  };
}
